/**
 * NB AJAX Cart — drawer controller.
 *
 * Owns cart mutations (add / change / note) and re-renders the drawer through
 * the Section Rendering API. Dialog lifecycle (open, close, backdrop, scroll
 * lock, desktop squeeze) stays with Horizon's <theme-drawer>, so the header
 * cart button and Shopify's `openCart` standard action keep working untouched.
 *
 * Cart mutations are announced with Shopify's standard
 * `shopify:cart:lines-update` event so <cart-icon> updates its bubble and any
 * app listening on the standard events bundle stays in sync.
 */

const ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** Resolve the standard-events bundle lazily; never let its absence break the cart. */
let eventsModule;
async function standardEvents() {
  if (eventsModule !== undefined) return eventsModule;
  try {
    eventsModule = await import('@shopify/events');
  } catch (_) {
    eventsModule = null;
  }
  return eventsModule;
}

/**
 * Dispatches `shopify:cart:lines-update` around a cart mutation.
 *
 * The event carries a promise that consumers await for the settled cart, which
 * is exactly how <cart-icon> reads the new item count. When the events bundle
 * is unavailable we still run the mutation — we just skip the announcement and
 * fall back to nudging the bubble by hand.
 *
 * @param {'add'|'remove'|'update'} action
 * @param {Array<object>} lines
 * @param {() => Promise<object>} run - performs the mutation, resolves to an AJAX cart
 * @returns {Promise<object>} the AJAX cart
 */
async function withCartEvent(action, lines, run) {
  const mod = await standardEvents();
  const Ctor = mod && mod.CartLinesUpdateEvent;

  if (!Ctor || typeof Ctor.createPromise !== 'function') {
    const cart = await run();
    syncCartIcon(cart);
    return cart;
  }

  const { promise, resolve, reject } = Ctor.createPromise();

  document.dispatchEvent(
    new Ctor({ action, context: 'dialog', lines, promise, detail: { source: 'nb-cart' } })
  );

  try {
    const cart = await run();
    const summary =
      typeof Ctor.createCartFromAjaxResponse === 'function'
        ? Ctor.createCartFromAjaxResponse(cart)
        : null;
    resolve({ cart: summary, detail: { source: 'nb-cart', itemCount: cart.item_count } });
    if (!summary) syncCartIcon(cart);
    return cart;
  } catch (error) {
    reject(error);
    throw error;
  }
}

/** Fallback bubble update for when the standard events bundle is missing. */
function syncCartIcon(cart) {
  if (!cart || typeof cart.item_count !== 'number') return;
  const icon = document.querySelector('cart-icon');
  if (icon && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count);
}

async function postJSON(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = data && data.description ? data.description : 'Cart update failed';
    throw Object.assign(new Error(message), { data });
  }

  return data;
}

/**
 * Fills one recommendation slot from Shopify's Product Recommendations API —
 * the endpoint the Search & Discovery app's complementary/related products feed.
 *
 * `intent=complementary` returns nothing unless a merchant has set complementary
 * products for that specific product, so an optional fallback retries with
 * `intent=related`, which is always populated algorithmically.
 *
 * @param {HTMLElement} slot
 */
async function fillRecommendationSlot(slot) {
  const d = slot.dataset;
  if (!d.seed || slot.dataset.nbRecDone === '1') return;
  slot.dataset.nbRecDone = '1';

  const sectionId = d.layout === 'rows' ? 'nb-cart-rec-rows' : 'nb-cart-rec-cards';
  const limit = Math.max(1, Number(d.limit) || 4);

  // Ask for extra rows: products already in the cart get filtered out below,
  // and the API has no exclude parameter.
  const fetchLimit = Math.min(10, limit + 4);

  const request = (intent) =>
    fetch(
      `${ROOT()}recommendations/products?product_id=${encodeURIComponent(d.seed)}` +
        `&limit=${fetchLimit}&intent=${intent}&section_id=${sectionId}`,
      { headers: { Accept: 'text/html' } }
    ).then((r) => (r.ok ? r.text() : ''));

  let html = await request(d.intent || 'related');
  let list = parseList(html);

  if (!list && d.intent === 'complementary' && d.fallback === '1') {
    list = parseList(await request('related'));
  }

  if (!list) return;

  const exclude = d.exclude || '';
  let kept = 0;

  for (const card of [...list.querySelectorAll('[data-nb-rec-card]')]) {
    const inCart = exclude.includes(`,${card.dataset.productId},`);
    if (inCart || kept >= limit) {
      card.remove();
      continue;
    }

    const button = card.querySelector('[data-nb-add]');
    const link = card.querySelector('[data-nb-rec-link]');
    const multi = card.dataset.multi === '1';
    // Adding an arbitrary variant of a multi-variant product would put the
    // wrong size in the cart, so those link out unless the merchant opted in.
    const canAdd = button && (!multi || d.variantMode === 'add');

    if (canAdd) {
      link?.remove();
      button.dataset.nbAddedLabel = d.btnAddedLabel || '';
      const label = button.querySelector('[data-nb-add-label]');
      if (label) label.textContent = d.btnLabel || '';
    } else {
      button?.remove();
      if (link) link.textContent = d.linkLabel || '';
    }

    const nudge = card.querySelector('[data-nb-rec-nudge]');
    if (nudge) {
      const wanted = d.nudge && (d.nudgeFirstOnly !== '1' || kept === 0);
      if (wanted) {
        nudge.textContent = d.nudge;
        nudge.hidden = false;
      } else {
        nudge.remove();
      }
    }

    kept++;
  }

  if (!kept) return;

  if (d.heading) {
    const heading = document.createElement('p');
    heading.className = 'nb-cart__group-title';
    heading.textContent = d.heading;
    slot.appendChild(heading);
  }
  slot.appendChild(list);
  slot.hidden = false;
}

/** @returns {Element | null} the rendered list element, or null when empty. */
function parseList(html) {
  if (!html || !html.trim()) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const list = doc.querySelector('.nb-cart__cs, .nb-cart__ups');
  return list && list.querySelector('[data-nb-rec-card]') ? list : null;
}

class NbCart extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** Guards against overlapping mutations racing each other's re-renders. */
  #pending = 0;
  #noteTimer = 0;

  get sectionId() {
    return this.dataset.sectionId;
  }

  get drawer() {
    return this.closest('theme-drawer');
  }

  connectedCallback() {
    // Fresh controller per connection — a controller aborted by a previous
    // disconnect silently drops every listener registered with its signal.
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });

    // Refresh (and optionally open) when anything else on the page touches the
    // cart — PDP add-to-cart, quick-add, or a third-party app.
    document.addEventListener('shopify:cart:lines-update', this.#onExternalUpdate, { signal });

    // Recommendations cost an extra request, so they wait until the drawer is
    // actually opened rather than loading on every page view.
    this.drawer?.addEventListener('theme-drawer:open', this.#hydrateRecommendations, { signal });
    if (this.drawer?.hasAttribute('open')) this.#hydrateRecommendations();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#noteTimer);
  }

  #onExternalUpdate = (event) => {
    const detail = event.detail;
    if (detail && detail.source === 'nb-cart') return; // our own mutation, already handled

    const shouldOpen = this.hasAttribute('auto-open') && event.action === 'add';

    Promise.resolve(event.promise)
      .then(() => this.refresh())
      .then(() => {
        if (!shouldOpen) return;
        const drawer = this.drawer;
        // Don't pop the drawer over a modal that's still up (quick-add): wait
        // for it to close so focus restoration runs in the right order.
        const sourceModal =
          event.target instanceof Element ? event.target.closest('dialog:modal') : null;

        if (sourceModal && sourceModal.open) {
          sourceModal.addEventListener('close', () => drawer && drawer.open && drawer.open(), {
            once: true,
          });
        } else if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) {
          drawer.open();
        }
      })
      .catch((error) => {
        if (error && error.name !== 'AbortError') console.warn('[nb-cart]', error);
      });
  };

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Horizon's declarative `on:click="#cart-drawer/close"` binding is provided
    // by its Component base class, which this element deliberately doesn't
    // extend — so close buttons are wired here instead.
    if (target.closest('[data-nb-close]')) {
      event.preventDefault();
      const drawer = this.drawer;
      if (drawer && typeof drawer.close === 'function') drawer.close();
      return;
    }

    const stepper = target.closest('[data-nb-step]');
    if (stepper) {
      event.preventDefault();
      const row = stepper.closest('[data-nb-line]');
      if (!row) return;
      const delta = Number(stepper.dataset.nbStep);
      const current = Number(row.dataset.nbQuantity || '0');
      this.#changeLine(row.dataset.nbLine, Math.max(0, current + delta), row);
      return;
    }

    const remove = target.closest('[data-nb-remove]');
    if (remove) {
      event.preventDefault();
      const row = remove.closest('[data-nb-line]');
      if (row) this.#changeLine(row.dataset.nbLine, 0, row);
      return;
    }

    const add = target.closest('[data-nb-add]');
    if (add) {
      event.preventDefault();
      this.#addVariant(add);
    }
  };

  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-nb-quantity-input]')) return;

    const row = input.closest('[data-nb-line]');
    if (!row) return;

    const quantity = Math.max(0, Math.floor(Number(input.value)));
    if (Number.isNaN(quantity)) {
      input.value = row.dataset.nbQuantity || '1';
      return;
    }
    this.#changeLine(row.dataset.nbLine, quantity, row);
  };

  #onInput = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLTextAreaElement) || !field.matches('[data-nb-note]')) return;

    // Debounce so a burst of keystrokes is one request, and never re-render the
    // drawer afterwards — that would blow away the textarea the user is in.
    clearTimeout(this.#noteTimer);
    this.#noteTimer = window.setTimeout(() => {
      postJSON(`${ROOT()}cart/update.js`, { note: field.value }).catch((error) =>
        console.warn('[nb-cart] note update failed:', error)
      );
    }, 500);
  };

  #onSubmit = (event) => {
    const form = event.target;
    if (form instanceof HTMLFormElement && form.matches('[data-nb-checkout-form]')) return; // let it post
  };

  /**
   * @param {string} key - cart line key
   * @param {number} quantity
   * @param {Element} row
   */
  async #changeLine(key, quantity, row) {
    if (!key) return;

    this.#setBusy(row, true);
    const action = quantity === 0 ? 'remove' : 'update';

    try {
      await withCartEvent(action, [{ id: key, quantity }], () =>
        postJSON(`${ROOT()}cart/change.js`, { id: key, quantity })
      );
      await this.refresh();
    } catch (error) {
      this.#setBusy(row, false);
      this.#showError(error);
    }
  }

  /** @param {Element} button */
  async #addVariant(button) {
    const id = button.dataset.nbAdd;
    if (!id) return;

    const quantity = Number(button.dataset.nbAddQuantity || '1');
    const label = button.querySelector('[data-nb-add-label]');
    const done = button.dataset.nbAddedLabel;

    button.setAttribute('disabled', '');
    button.setAttribute('aria-busy', 'true');

    try {
      await withCartEvent('add', [{ merchandiseId: id, quantity }], () =>
        postJSON(`${ROOT()}cart/add.js`, { items: [{ id: Number(id), quantity }] })
      );

      if (label && done) label.textContent = done;
      button.classList.add('is-added');
      await this.refresh();
    } catch (error) {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
      this.#showError(error);
    }
  }

  #setBusy(row, busy) {
    if (!row) return;
    row.classList.toggle('is-busy', busy);
    row.querySelectorAll('button, input').forEach((el) => {
      if (busy) el.setAttribute('disabled', '');
      else el.removeAttribute('disabled');
    });
  }

  #showError(error) {
    const slot = this.querySelector('[data-nb-error]');
    const message = (error && error.message) || 'Something went wrong.';
    if (!slot) {
      console.warn('[nb-cart]', message);
      return;
    }
    slot.textContent = message;
    slot.hidden = false;
    window.setTimeout(() => {
      slot.hidden = true;
    }, 5000);
  }

  /**
   * Re-renders the drawer body from the Section Rendering API.
   *
   * Only the inner region is swapped so <theme-drawer> and the <dialog> keep
   * their identity — replacing those would drop the open state mid-interaction.
   */
  async refresh() {
    const sectionId = this.sectionId;
    if (!sectionId) return;

    const ticket = ++this.#pending;
    const url = `${ROOT()}?section_id=${encodeURIComponent(sectionId)}`;

    const response = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Section render failed (${response.status})`);

    const html = await response.text();

    // A newer mutation already landed; its render supersedes this one.
    if (ticket !== this.#pending) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('[data-nb-cart-inner]');
    const current = this.querySelector('[data-nb-cart-inner]');

    if (!next || !current) return;

    const scrollHost = current.querySelector('[data-nb-scroll]');
    const scrollTop = scrollHost ? scrollHost.scrollTop : 0;

    current.replaceWith(next);

    const restored = this.querySelector('[data-nb-scroll]');
    if (restored && scrollTop) restored.scrollTop = scrollTop;

    // The swap brought in fresh, unfilled slots.
    this.#hydrateRecommendations();

    this.dispatchEvent(new CustomEvent('nb-cart:refreshed', { bubbles: true }));
  }

  #hydrateRecommendations = () => {
    for (const slot of this.querySelectorAll('[data-nb-rec-slot]')) {
      fillRecommendationSlot(slot).catch((error) =>
        console.warn('[nb-cart] recommendations failed:', error)
      );
    }
  };
}

if (!customElements.get('nb-cart')) customElements.define('nb-cart', NbCart);

/**
 * Theme editor: open the drawer when the merchant selects the section so its
 * settings are actually previewable, and close it again on deselect.
 */
if (window.Shopify && window.Shopify.designMode) {
  const drawerFor = (event) => {
    const el = event.target instanceof Element ? event.target.querySelector('nb-cart') : null;
    return el ? el.closest('theme-drawer') : null;
  };

  document.addEventListener('shopify:section:select', (event) => {
    const drawer = drawerFor(event);
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = drawerFor(event);
    if (drawer && typeof drawer.close === 'function') drawer.close();
  });

  document.addEventListener('shopify:section:load', (event) => {
    const el = event.target instanceof Element ? event.target.querySelector('nb-cart') : null;
    const drawer = el ? el.closest('theme-drawer') : null;
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });
}
